import { useContext, useMemo, useState } from "react";
import {
  AppBar,
  Badge,
  Box,
  IconButton,
  Popover,
  Toolbar,
  Tooltip,
  Typography,
  useTheme,
} from "@mui/material";
import { alpha } from "@mui/material/styles";
import { useHistory } from "react-router-dom";

import MenuRoundedIcon from "@mui/icons-material/MenuRounded";
import DarkModeOutlinedIcon from "@mui/icons-material/DarkModeOutlined";
import LightModeOutlinedIcon from "@mui/icons-material/LightModeOutlined";
import NotificationsNoneOutlinedIcon from "@mui/icons-material/NotificationsNoneOutlined";
import PaletteOutlinedIcon from "@mui/icons-material/PaletteOutlined";
import CheckRoundedIcon from "@mui/icons-material/CheckRounded";

import logoLight from "../../images/logos/NewLogoLight.png";
import logoDark from "../../images/logos/NewLogoDark.png";
import { NotificationContext } from "../../App";
import { useSidebarAccent } from "./SidebarAccentContext";
import withAuth from "../../Hoc/withAuth";

const TOPBAR_HEIGHT = 60;

/**
 * Mobile top bar. The navigation drawer itself lives in NavBarWDrawer
 * (a themed temporary Drawer); this bar only toggles it via onMenuClick.
 */
const MobileSidebarNav = ({ setTheme, currentTheme, onMenuClick }) => {
  const theme  = useTheme();
  const router = useHistory();
  const isDark = theme.palette.mode === "dark";

  const { accent, setAccent, palette } = useSidebarAccent();
  const [paletteAnchor, setPaletteAnchor] = useState(null);
  const paletteOpen = Boolean(paletteAnchor);

  const { notifications } = useContext(NotificationContext) || { notifications: [] };
  const unread = useMemo(
    () => (notifications || []).filter((n) => n.read === false).length,
    [notifications],
  );

  const iconBtnSx = {
    color:        "text.secondary",
    bgcolor:      alpha(theme.palette.text.primary, 0.04),
    borderRadius: 2,
    "&:hover": {
      color:   theme.palette.primary.main,
      bgcolor: alpha(theme.palette.primary.main, 0.12),
    },
  };

  return (
    <AppBar
      position="fixed"
      elevation={0}
      sx={{
        bgcolor:        alpha(theme.palette.background.paper, isDark ? 0.85 : 0.9),
        backdropFilter: "blur(8px)",
        color:          "text.primary",
        borderBottom:   `1px solid ${theme.palette.divider}`,
        borderRadius:   0,
        zIndex:         theme.zIndex.drawer + 1,
      }}
    >
      <Toolbar sx={{ justifyContent: "space-between", minHeight: TOPBAR_HEIGHT, px: 1.5 }}>
        {/* Left — menu + logo */}
        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
          <Tooltip title="Menu" arrow>
            <IconButton
              size="small"
              aria-label="open navigation"
              onClick={onMenuClick}
              sx={iconBtnSx}
            >
              <MenuRoundedIcon fontSize="small" />
            </IconButton>
          </Tooltip>

          <Box
            component="img"
            src={currentTheme ? logoLight : logoDark}
            alt="Chalk It Up"
            onClick={() => router.push("/")}
            sx={{ height: 28, width: "auto", objectFit: "contain", cursor: "pointer" }}
          />
        </Box>

        {/* Right — accent picker + theme toggle + notifications */}
        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
          <Tooltip title="Accent colour" arrow>
            <IconButton
              size="small"
              aria-label="sidebar accent"
              onClick={(e) => setPaletteAnchor(e.currentTarget)}
              sx={iconBtnSx}
            >
              <PaletteOutlinedIcon fontSize="small" />
            </IconButton>
          </Tooltip>

          <Tooltip title={`Switch to ${currentTheme ? "dark" : "light"} mode`} arrow>
            <IconButton
              size="small"
              aria-label="toggle theme"
              onClick={() => setTheme && setTheme(!currentTheme)}
              sx={iconBtnSx}
            >
              {currentTheme ? (
                <DarkModeOutlinedIcon fontSize="small" />
              ) : (
                <LightModeOutlinedIcon fontSize="small" />
              )}
            </IconButton>
          </Tooltip>

          <Tooltip title="Notifications" arrow>
            <IconButton
              size="small"
              aria-label="notifications"
              onClick={() => router.push("/notifications")}
              sx={iconBtnSx}
            >
              <Badge
                badgeContent={unread}
                color="error"
                overlap="circular"
                sx={{
                  "& .MuiBadge-badge": {
                    fontSize:  "0.62rem",
                    height:    16,
                    minWidth:  16,
                    fontWeight: 700,
                  },
                }}
              >
                <NotificationsNoneOutlinedIcon fontSize="small" />
              </Badge>
            </IconButton>
          </Tooltip>
        </Box>
      </Toolbar>

      {/* Sidebar accent palette popover */}
      <Popover
        anchorEl={paletteAnchor}
        open={paletteOpen}
        onClose={() => setPaletteAnchor(null)}
        transformOrigin={{ horizontal: "right", vertical: "top" }}
        anchorOrigin={{ horizontal: "right", vertical: "bottom" }}
        PaperProps={{
          sx: {
            mt: 1,
            p: 2,
            width: 220,
            border: `1px solid ${theme.palette.divider}`,
            backgroundImage: "none",
            backgroundColor: isDark
              ? alpha(theme.palette.background.paper, 0.97)
              : theme.palette.background.paper,
          },
        }}
      >
        <Typography
          variant="caption"
          sx={{
            display: "block",
            color: "text.secondary",
            fontWeight: 700,
            letterSpacing: "0.08em",
            textTransform: "uppercase",
            fontSize: "0.62rem",
            mb: 1,
          }}
        >
          Accent colour
        </Typography>

        <Box sx={{ display: "grid", gridTemplateColumns: "repeat(4, 1fr)", gap: 1 }}>
          {palette.map((c) => {
            const selected = c.value === accent;
            return (
              <Tooltip key={c.value} title={c.name} arrow>
                <Box
                  role="button"
                  aria-label={c.name}
                  onClick={() => {
                    setAccent(c.value);
                    setPaletteAnchor(null);
                  }}
                  sx={{
                    width: 40,
                    height: 40,
                    borderRadius: "50%",
                    cursor: "pointer",
                    backgroundColor: c.value,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    color: "#fff",
                    border: `2px solid ${selected ? theme.palette.text.primary : "transparent"}`,
                    boxShadow: `0 4px 14px ${alpha(c.value, 0.45)}`,
                    transition: "transform .15s ease, border-color .15s ease",
                    "&:hover": { transform: "scale(1.08)" },
                  }}
                >
                  {selected && <CheckRoundedIcon sx={{ fontSize: 20 }} />}
                </Box>
              </Tooltip>
            );
          })}
        </Box>
      </Popover>
    </AppBar>
  );
};

export default withAuth(MobileSidebarNav);
